import { inject, Injectable } from '@angular/core';
import {
  AuthenticatedUser,
  CacheService,
  LoginAuthCodeRequest,
  MemberLoginRequest,
  NewPasswordRequest,
  RefreshSessionRequest,
  SessionService,
} from '@pgaoc/shared-lib';
import { catchError, Observable, of, switchMap, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MemberAuthApi } from '../api/member-auth-api';

@Injectable({
  providedIn: 'root',
})
export class MemberAuthRepository {
  private memberAuthApi = inject(MemberAuthApi);
  private sessionService = inject(SessionService);
  private cacheService = inject(CacheService);
  private rememberMeMemberIdCacheKey = 'rememberMeMemberIdCacheKey';

  login(request: MemberLoginRequest): Observable<AuthenticatedUser> {
    return this.memberAuthApi.login(request);
  }

  resetPassword(memberId: number): Observable<void> {
    return this.memberAuthApi.resetPassword(memberId);
  }

  submitMFACode(request: LoginAuthCodeRequest): Observable<AuthenticatedUser> {
    return this.memberAuthApi.submitMFACode(request);
  }

  setNewPassword(memberId: number, newPasswordRequest: NewPasswordRequest): Observable<void> {
    return this.memberAuthApi.setNewPassword(memberId, newPasswordRequest);
  }

  resendWelcomeEmail(email: string): Observable<void> {
    return this.memberAuthApi.resendWelcomeEmail(email);
  }

  isAuthenticated(): Observable<AuthenticatedUser | null> {
    return this.sessionService.authenticatedUser$.pipe(
      take(1),
      switchMap(user => {
        const cachedSession = this.sessionService.getCachedAuthenticatedUser();
        if (!user && !!cachedSession) {
          user = cachedSession;
          this.sessionService.setAuthenticatedUser(user);
        }
        if (user?.userSession && !!user?.userSession?.accessToken) {
          return of(user);
        }
        return this.refreshSession();
      }),
    );
  }

  refreshSession(): Observable<AuthenticatedUser | null> {
    return this.sessionService.authenticatedUser$.pipe(
      take(1),
      switchMap(currentUser => {
        if (currentUser?.userSession?.refreshToken) {
          this.sessionService.setRefreshingSession(true);
          const req: RefreshSessionRequest = {
            refreshToken: currentUser.userSession.refreshToken,
          };
          return this.memberAuthApi.refreshSession(req, currentUser).pipe(
            tap(user => {
              this.sessionService.setAuthenticatedUser(user);
              this.sessionService.setRefreshingSession(false);
            }),
          );
        } else {
          this.sessionService.setRefreshingSession(false);
          return of(null);
        }
      }),
      catchError(error => {
        this.sessionService.setRefreshingSession(false);
        if (error.status === 400) {
          this.sessionService.destroySession.next(true);
        }
        // Pass other errors along
        throw error;
      }),
    );
  }

  rememberLoginMemberId(memberId: number | null) {
    if (memberId === null) {
      this.cacheService.removeData(this.rememberMeMemberIdCacheKey);
    } else {
      this.cacheService.storeData(this.rememberMeMemberIdCacheKey, memberId);
    }
  }

  getRememberedLoginMemberId(): number | null {
    return this.cacheService.loadData(this.rememberMeMemberIdCacheKey);
  }
}
